import { fr, type Dictionary } from './fr.js';

/**
 * Couche d'internationalisation minimaliste et typee.
 *
 * Le dictionnaire francais est la reference : `t` est type a partir de sa
 * structure, une cle inexistante est donc une erreur de compilation. Ajouter une
 * langue consiste a fournir un objet de meme forme et a l'enregistrer ici.
 */
export const AVAILABLE_LOCALES = ['fr'] as const;
export type Locale = (typeof AVAILABLE_LOCALES)[number];

const dictionaries: Record<Locale, Dictionary> = { fr };

let currentLocale: Locale = 'fr';

export function setLocale(locale: Locale): void {
  currentLocale = locale;
  document.documentElement.lang = locale;
}

export function getLocale(): Locale {
  return currentLocale;
}

/** Acces au dictionnaire courant. Utilisation : `t().home.heroTitle`. */
export function t(): Dictionary {
  return dictionaries[currentLocale];
}

const numberFormatters = new Map<string, Intl.NumberFormat>();

/** Formate un nombre selon la langue courante (separateurs, decimales). */
export function formatNumber(value: number, fractionDigits = 0): string {
  const key = `${currentLocale}:${fractionDigits}`;
  let formatter = numberFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(currentLocale, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
    numberFormatters.set(key, formatter);
  }
  return formatter.format(value);
}

/** Formate une duree en secondes sous la forme « 1 h 24 min » ou « 45 s ». */
export function formatDuration(totalSeconds: number): string {
  const labels = t().units;
  const seconds = Math.max(0, Math.round(totalSeconds));

  if (seconds < 60) return `${seconds} ${labels.seconds}`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} ${labels.minutes}`;

  const hours = Math.floor(minutes / 60);
  const restMinutes = minutes % 60;
  return restMinutes === 0
    ? `${hours} ${labels.hours}`
    : `${hours} ${labels.hours} ${restMinutes} ${labels.minutes}`;
}
